/**
 * CUSTOMER-R4.15-F — canonical product media delivery variants.
 * Prom CDN encodes size in filename token `_wNNN_hNNN_`; do not append resize query params.
 */

export type ProductMediaVariant = "original" | "card" | "mini";

const PROM_SIZE_TOKEN = /_w\d+_h\d+_/i;

const VARIANT_SIZE: Record<ProductMediaVariant, string> = {
  original: "_w640_h640_",
  card: "_w200_h200_",
  mini: "_w200_h200_",
};

/** Swap Prom `_wNNN_hNNN_` token when present; pass through non-Prom URLs unchanged. */
export function resolveProductMediaUrl(
  raw: string | null | undefined,
  variant: ProductMediaVariant = "card",
): string | null {
  const url = String(raw ?? "").trim();
  if (!url) return null;
  if (!/^https?:\/\//i.test(url)) return url;
  if (!PROM_SIZE_TOKEN.test(url)) return url;
  return url.replace(PROM_SIZE_TOKEN, VARIANT_SIZE[variant]);
}

/** Grid/list cards — optimized preview (~200px). */
export function cardImageUrl(raw: string | null | undefined): string | null {
  return resolveProductMediaUrl(raw, "card");
}

/** Search suggest / cart / checkout mini previews (~200px source, ~48–56px display). */
export function miniImageUrl(raw: string | null | undefined): string | null {
  return resolveProductMediaUrl(raw, "mini");
}

/** PDP / zoom — prefer larger Prom derivative when available. */
export function pdpImageUrl(raw: string | null | undefined): string | null {
  return resolveProductMediaUrl(raw, "original");
}

export type ProductImageLoadPriority = "lazy" | "eager";

export type ProductImageFetchPriority = "high" | "low" | "auto";

/** First 2–4 visible cards: eager; first two also high fetchPriority. Rest lazy. */
export function gridImageLoadingHints(index: number): {
  loading: ProductImageLoadPriority;
  fetchPriority?: ProductImageFetchPriority;
} {
  if (index <= 1) return { loading: "eager", fetchPriority: "high" };
  if (index <= 3) return { loading: "eager" };
  return { loading: "lazy" };
}
